/**
 * This class handles all the performance calls.
 *
 * Usage: `context.$trackers.perf.mark(label)` to mark an event
 * `context.$trackers.perf.measure(measureLabel, label-1, label-2)` to measure
 * the time between two previously marked events
 *
 * @see        https://developer.mozilla.org/fr/docs/Web/API/Performance
 *
 * @class      PerformanceService (name)
 */
export class PerformanceService {
  /**
   * Constructs a new instance.
   * This is used to have the correct reference to performance between server and client.
   * The eval() here may seems scary but it's only used to prevent Webpack.
   *
   * @see https://stackoverflow.com/a/67288823
   */
  constructor() {
    if (typeof performance === 'undefined') {
      // Node.js.
      this.performance = eval('require')('perf_hooks').performance;
    } else {
      // Browser.
      this.performance = performance;
    }
  }

  /**
   * Sets a mark for the given label.
   *
   * @param      {string}  label   The label.
   */
  mark(label) {
    try {
      this.performance.clearMarks(label);
      this.performance.mark(label);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Measures the time between the two given marks.
   *
   * @param      {string}  label      The label of the measure.
   * @param      {string}  startMark  The start mark
   * @param      {string}  endMark    The end mark
   */
  measure(label, startMark, endMark) {
    try {
      this.performance.clearMeasures(label);
      this.performance.measure(label, startMark, endMark);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Will display the result of a given label and type in the console.
   * Useful to debug.
   *
   * @param      {string}  label   The label of the mark or measure to display
   * @param      {string}  type    The type to display. 'mark' or 'measure'
   */
  display(label, type) {
    console.log(this.performance.getEntriesByName(label, type)[0]);
  }
}
